<p-dialog
  header="Import Collections from CSV"
  [(visible)]="visible"
  (onHide)="close()"
  [modal]="true"
  [style]="{ width: '900px' }"
  [closable]="!isImporting"
>
  <p-toast></p-toast>

  <!-- STEP 1: Upload -->
  <ng-container *ngIf="step === 'upload'">
    <div class="ordering-hint p-mb-3">
      <i class="pi pi-info-circle"></i>
      <span>Items are grouped into collections in the order they appear in the CSV, top to bottom — put your highest-priority items (e.g. least-used) first.</span>
    </div>

    <div class="import-header p-mb-3">
      <label class="file-label">
        <i class="pi pi-upload p-mr-1"></i> Choose CSV file
        <input type="file" accept=".csv" (change)="onFileChange($event)" />
      </label>
      <button pButton type="button" label="Download Example CSV" icon="pi pi-download"
        class="p-button-text p-button-sm p-ml-2"
        pTooltip="Items are grouped top to bottom in the order they appear in the CSV."
        (click)="downloadTemplate()">
      </button>
    </div>

    <div class="config-fields p-mb-3">
      <div class="p-field">
        <label for="itemsPerCollection">Items per collection</label>
        <p-inputNumber inputId="itemsPerCollection" [(ngModel)]="itemsPerCollection" [min]="1"></p-inputNumber>
      </div>
      <div class="p-field">
        <label for="nameTemplate">Collection name template</label>
        <input pInputText id="nameTemplate" type="text" [(ngModel)]="nameTemplate" placeholder="e.g. Sunset Items #" />
        <small class="field-hint"><code>#</code> is replaced with the collection number (1, 2, 3…).</small>
      </div>
    </div>

    <p *ngIf="parsedRows.length" class="parsed-count">
      <i class="pi pi-check-circle p-mr-1 success-icon"></i>
      {{ parsedRows.length }} row(s) loaded — click <strong>Validate</strong> to check the CSV structure.
    </p>

    <div *ngIf="validation && !validation.valid" class="validate-banner validate-banner-error p-mb-3">
      <strong>CSV validation failed:</strong>
      <ul>
        <li *ngFor="let e of validation.errors">{{ e }}</li>
      </ul>
    </div>
    <div *ngIf="validation && validation.valid" class="validate-banner validate-banner-success p-mb-3">
      <i class="pi pi-check-circle"></i>
      <span>{{ validation.orderedIds.length }} valid item id(s) found.</span>
      <ul *ngIf="validation.warnings.length">
        <li *ngFor="let w of validation.warnings">{{ w }}</li>
      </ul>
    </div>
  </ng-container>

  <!-- STEP 2: Preview -->
  <ng-container *ngIf="step === 'preview'">
    <p class="preview-label">
      Showing {{ parsedRows.length }} row(s) in CSV order (top to bottom = collection item order).
    </p>
    <p-table [value]="parsedRows" [paginator]="parsedRows.length > 10" [rows]="10" styleClass="p-datatable-sm preview-table">
      <ng-template pTemplate="header">
        <tr>
          <th style="width:60px">#</th>
          <th>item_id</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r let-i="rowIndex">
        <tr>
          <td>{{ i + 1 }}</td>
          <td>{{ r.item_id }}</td>
        </tr>
      </ng-template>
    </p-table>
  </ng-container>

  <!-- STEP 3: Dry Run -->
  <ng-container *ngIf="step === 'dry-run'">
    <div *ngIf="dryRunErrors.length" class="validate-banner validate-banner-error p-mb-3">
      <strong>Dry run found problems — nothing will be imported until these are fixed:</strong>
      <ul>
        <li *ngFor="let e of dryRunErrors">{{ e }}</li>
      </ul>
    </div>

    <p class="preview-label" pTooltip="Each collection's items keep the order they appear in the CSV.">
      {{ plannedCollections.length }} collection(s) planned, items in each kept in CSV order.
    </p>

    <p-table [value]="plannedCollections" [paginator]="plannedCollections.length > 10" [rows]="10" styleClass="p-datatable-sm">
      <ng-template pTemplate="header">
        <tr>
          <th>Name</th>
          <th style="width:100px">Item count</th>
          <th>First item_id</th>
          <th>Last item_id</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-p>
        <tr>
          <td>{{ p.name }}</td>
          <td>{{ p.ids.length }}</td>
          <td>{{ p.ids[0] }}</td>
          <td>{{ p.ids[p.ids.length - 1] }}</td>
        </tr>
      </ng-template>
    </p-table>
  </ng-container>

  <!-- STEP 4: Importing -->
  <ng-container *ngIf="step === 'importing'">
    <p-progressBar [value]="importProgress"></p-progressBar>
    <small class="p-ml-1">{{ results.length }} / {{ plannedCollections.length }} collection(s) processed</small>
  </ng-container>

  <!-- STEP 5: Results -->
  <ng-container *ngIf="step === 'results'">
    <div class="result-summary p-mb-3">
      <p-tag severity="success" [value]="successCount + ' created'" class="p-mr-1"></p-tag>
      <p-tag *ngIf="failureCount" severity="danger" [value]="failureCount + ' failed'"></p-tag>
    </div>
    <p-table [value]="results" [paginator]="results.length > 15" [rows]="15" styleClass="p-datatable-sm">
      <ng-template pTemplate="header">
        <tr>
          <th>Name</th>
          <th style="width:90px">Item count</th>
          <th>Status</th>
          <th style="width:90px">Collection</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr [class.row-success]="!r.error" [class.row-invalid]="r.error">
          <td>{{ r.name }}</td>
          <td>{{ r.itemCount }}</td>
          <td>
            <p-tag *ngIf="!r.error" severity="success" value="created"></p-tag>
            <p-tag *ngIf="r.error" severity="danger" [value]="r.error"></p-tag>
          </td>
          <td>
            <a *ngIf="!r.error" [routerLink]="['/collections', r.id]" target="_blank" pTooltip="Open in a new tab">
              <i class="pi pi-external-link p-mr-1"></i>View
            </a>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </ng-container>

  <ng-template pTemplate="footer">
    <ng-container *ngIf="step === 'upload'">
      <button pButton type="button" label="Cancel" class="p-button-text" (click)="close()"></button>
      <button pButton type="button" label="Validate" icon="pi pi-check-square"
        [disabled]="!canValidate" (click)="validate()">
      </button>
    </ng-container>
    <ng-container *ngIf="step === 'preview'">
      <button pButton type="button" label="Back" icon="pi pi-arrow-left" class="p-button-text" (click)="back()"></button>
      <button pButton type="button" label="Dry Run" icon="pi pi-refresh"
        [loading]="isRunningDryRun" (click)="runDryRun()">
      </button>
    </ng-container>
    <ng-container *ngIf="step === 'dry-run'">
      <button pButton type="button" label="Back" icon="pi pi-arrow-left" class="p-button-text" (click)="back()"></button>
      <button pButton type="button" label="Re-run Dry Run" icon="pi pi-refresh" class="p-button-text" (click)="runDryRun()"></button>
      <button pButton type="button" [label]="'Import ' + plannedCollections.length + ' collections'" icon="pi pi-check"
        [disabled]="!dryRunPassed" (click)="runImport()">
      </button>
    </ng-container>
    <ng-container *ngIf="step === 'results'">
      <button pButton type="button" label="Close" (click)="close()"></button>
    </ng-container>
  </ng-template>
</p-dialog>
